import { useEffect, useState } from "react";
import { Box, Divider, Drawer, IconButton, LinearProgress, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import makeStyles from "@mui/styles/makeStyles";

import { ReusableRTP } from "@APP/types";
import { API } from "@APP/services";
import { formatCurrency, formatDisplayedDate, formatUtcDate } from "@APP/utils";

import PaymentsTable from "./PaymentsTable";
import { accentFor } from "./statusAccent";

type Props = {
  rtp: ReusableRTP | null;
  open: boolean;
  onClose: () => void;
};

const useStyles = makeStyles((theme) => ({
  paper: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    // the paper itself never scrolls — only the payments section does
    overflow: "hidden",
    [theme.breakpoints.up("sm")]: {
      width: 520,
    },
    [theme.breakpoints.up("lg")]: {
      width: 600,
    },
  },
  header: {
    flexShrink: 0,
    padding: theme.spacing(3),
    color: theme.palette.common.white,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #013a8f 100%)`,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  statusPill: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "4px 12px",
    borderRadius: 999,
    color: theme.palette.common.white,
  },
  closeBtn: {
    color: theme.palette.common.white,
    marginTop: -8,
    marginRight: -8,
  },
  username: {
    fontWeight: 600,
    opacity: 0.92,
  },
  reference: {
    fontFamily: "'Roboto Mono', ui-monospace, monospace",
    fontSize: 12,
    letterSpacing: "0.04em",
    opacity: 0.7,
  },
  description: {
    fontWeight: 700,
    marginTop: theme.spacing(1.5),
  },
  amountRow: {
    display: "flex",
    alignItems: "baseline",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  amount: {
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
  },
  amountLabel: {
    fontSize: 13,
    opacity: 0.75,
  },
  bar: {
    height: 8,
    borderRadius: 999,
    marginTop: theme.spacing(1.5),
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    "& .MuiLinearProgress-bar": {
      borderRadius: 999,
      backgroundColor: theme.palette.secondary.main,
    },
  },
  details: {
    flexShrink: 0,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
  },
  detailLabel: {
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontSize: 10,
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  detailValue: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginTop: 2,
    wordBreak: "break-word",
  },
  payments: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: theme.spacing(3),
  },
}));

const yesNo = (value: boolean) => (value ? "Yes" : "No");

const ReusableRTPDetailDrawer = ({ rtp, open, onClose }: Props) => {
  const classes = useStyles();
  const [tipTotal, setTipTotal] = useState<number | null>(null);
  const [totalPaid, setTotalPaid] = useState<number | null>(null);

  useEffect(() => {
    setTipTotal(null);
    setTotalPaid(null);
    if (!rtp?.id || !rtp.allowTip) return;

    let cancelled = false;
    API.getReusableRTPPayments(rtp.id, { page: 0, entries: 1000 })
      .then(({ data }) => {
        if (cancelled || !data) return;
        let tips = 0;
        let paid = 0;
        for (const p of data) {
          const paidAmt = parseFloat(p.totalAmountPaid?.amount ?? p.amount?.amount ?? "0");
          const baseAmt = parseFloat(p.amount?.amount ?? "0");
          paid += paidAmt;
          tips += Math.max(0, paidAmt - baseAmt);
        }
        setTotalPaid(paid);
        setTipTotal(tips);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [rtp?.id, rtp?.allowTip]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ className: classes.paper }}>
      {rtp && (
        <>
          {(() => {
            const accent = accentFor(rtp.status);
            const received = Number(rtp.totalAmountReceived?.amount ?? 0);
            const requested = rtp.amountRequested ? Number(rtp.amountRequested.amount) : null;
            const currency = rtp.totalAmountReceived?.currency ?? "GBP";
            const percent =
              requested && requested > 0 ? Math.min(100, (received / requested) * 100) : null;
            const expiry = formatUtcDate(rtp.expiresAt, "PPp");
            const created = formatDisplayedDate(rtp.createdAt, "PPp");
            const displayAmount = rtp.allowTip && totalPaid != null ? totalPaid : received;

            const detailItems = [
              { label: "Payment links used", value: String(rtp.paymentLinksUsed) },
              { label: "Link expiry", value: expiry ?? "No expiry" },
              { label: "Customer name required", value: yesNo(rtp.requireCustomerName) },
              { label: "Tips allowed", value: yesNo(rtp.allowTip) },
              { label: "Created", value: created ?? "—" },
              { label: "Organisation ID", value: rtp.orgId },
            ];

            return (
              <>
                <Box className={classes.header}>
                  <Box className={classes.headerTop}>
                    <span className={classes.statusPill} style={{ backgroundColor: accent }}>
                      {rtp.status}
                    </span>
                    <IconButton
                      aria-label="Close details"
                      className={classes.closeBtn}
                      onClick={onClose}>
                      <CloseIcon />
                    </IconButton>
                  </Box>

                  <Typography variant="body2" className={classes.username}>
                    {rtp.username}
                  </Typography>
                  <Typography className={classes.reference}>{rtp.reference}</Typography>
                  <Typography variant="h5" className={classes.description}>
                    {rtp.description}
                  </Typography>

                  <Box className={classes.amountRow}>
                    <Typography variant="h4" className={classes.amount}>
                      {formatCurrency(String(displayAmount), currency)}
                    </Typography>
                    <Typography className={classes.amountLabel}>
                      received
                      {requested !== null
                        ? ` of ${formatCurrency(rtp.amountRequested!.amount, currency)} target`
                        : " · open amount"}
                    </Typography>
                  </Box>
                  {tipTotal != null && tipTotal > 0 && (
                    <Typography className={classes.amountLabel} style={{ marginTop: 4 }}>
                      + {formatCurrency(tipTotal.toFixed(2), currency)} in tips
                    </Typography>
                  )}
                  {percent !== null && (
                    <LinearProgress variant="determinate" value={percent} className={classes.bar} />
                  )}
                </Box>

                <Box className={classes.details}>
                  {detailItems.map((item) => (
                    <Box key={item.label}>
                      <Typography className={classes.detailLabel}>{item.label}</Typography>
                      <Typography className={classes.detailValue}>{item.value}</Typography>
                    </Box>
                  ))}
                </Box>

                <Divider />

                <Box className={classes.payments}>
                  <PaymentsTable
                    rtpId={rtp.id}
                    currency={currency}
                    allowTip={rtp.allowTip}
                    baseAmount={requested ?? undefined}
                  />
                </Box>
              </>
            );
          })()}
        </>
      )}
    </Drawer>
  );
};

export default ReusableRTPDetailDrawer;
